import React from "react";
import {
  getWebPUrl,
  getAvifUrl,
  OptimizedImageProps,
} from "../utils/image-optimization";

interface OptimizedImageComponentProps extends OptimizedImageProps {
  hero?: boolean;
  responsive?: boolean;
}

/**
 * Optimized image component with modern format support and performance optimizations
 */
export default function OptimizedImage({
  src,
  alt,
  width,
  height,
  className = "",
  priority = false,
  loading = "lazy",
  decoding = "async",
  sizes,
  srcSet,
  hero = false,
  responsive = true,
}: OptimizedImageComponentProps) {
  // Determine loading strategy
  const finalLoading = hero ? "eager" : loading;
  const finalPriority = hero || priority;

  // Generate responsive sizes if not provided
  const finalSizes =
    sizes ||
    (responsive
      ? "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
      : "100vw");

  // Generate optimized URLs
  const webpUrl = getWebPUrl(src);
  const avifUrl = getAvifUrl(src);

  return (
    <picture>
      {/* AVIF format for modern browsers */}
      <source type="image/avif" srcSet={avifUrl} sizes={finalSizes} />
      {/* WebP format for broader support */}
      <source type="image/webp" srcSet={webpUrl} sizes={finalSizes} />
      {/* Fallback to original format */}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        fetchpriority={finalPriority ? "high" : undefined}
        loading={finalLoading}
        decoding={decoding}
        className={className}
      />
    </picture>
  );
}
